import React, { useEffect, useState, useMemo } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { styled } from '@mui/material/styles'
import {
  Table,
  TableBody,
  TableContainer,
  TableRow,
  Paper,
  ToggleButton,
  Button,
  Stack,
  TableCell,
} from '@mui/material'
import DateRangeForm from '@components/DateRangeForm'
import { toStringByFormatting } from '@utils/dateUtils'
import DynamicHead from '@components/DynamicHead'

import {
  getReportList,
  getReportWithExcel
} from '@store/admin/report'

const StyledToggleButton = styled(ToggleButton)({
  padding: '5px 15px',
  '&.Mui-selected': {
    borderColor: '#1976d2',
    color: '#1976d2',
    backgroundColor: '#fff',
    '&:hover': {
      borderColor: '#1976d2',
      color: '#1976d2'
    }
  }
})

const StyledSearch = styled('div')({
  position: 'absolute',
  right: 20,
  top: 44
})

const StyledSearchForm = styled('div')({
  display: 'inline-block'
})

const StyledSearchBtn = styled(Button)({
  marginLeft: 10,
  marginTop: 15,
})

const ButtonGroup = styled(Stack)({
  padding: '10px 0',
  textAlign: 'right'
})

const ExcelButton = styled(Button)({
  position: 'absolute',
  left: 20
})

const headerList = [
  {key: 'companyName', name: '회사명', sort: true, width: '50%'},
  {key: 'member', name: '임원 변동', sort: true, width: '10%'},
  {key: 'company', name: '기업 변동', sort: true, width: '10%'},
  {key: 'etc', name: '변동 사유'},
]

const getThisWeek = () => {
  const curr = new Date()
  const first = curr.getDate() - curr.getDay() + 1
  const last = first + 6

  const firstDay = new Date().setDate(first)
  const lastDay = new Date().setDate(last)

  return [firstDay, lastDay]
}

const initData = {
  dateRange: getThisWeek()
}

const TableItem = ({
  item
}) => {
  return (
    <TableRow
      hover
      sx={{ '&:last-child td, &:last-child th': { border: 0 } }}
    >
      <TableCell component="th" scope="row" align="center">
        {item.companyName}
      </TableCell>
      <TableCell align="center">
        <StyledToggleButton
          color="primary"
          selected={item.member || false}
          exclusive
          size="small"
        >
          {item.member ? 'Y' : 'N'}
        </StyledToggleButton>
      </TableCell>
      <TableCell align="center">
        <StyledToggleButton
          color="primary"
          selected={item.company || false}
          exclusive
          size="small"
        >
          {item.company ? 'Y' : 'N'}
        </StyledToggleButton>
      </TableCell>
      <TableCell align="center" sx={{whiteSpace: 'pre-wrap'}}>{item.etc}</TableCell>
    </TableRow>
  )
}

const MonitoringTable = () => {
  const dispatch = useDispatch()
  const {
    reportList,
  } = useSelector(store => store.reportAdmin)
  const [ form, setForm ] = useState(initData)
  const [ sortDirection, setSortDirection ] = useState({});

  const handleChangeData = (key, value) => {
    if (value.length === 2) {

    }
    const updateForm = {}
    updateForm[key] = value
    setForm(Object.assign({}, form, updateForm))
  }

  const downloadExcels = () => {
    dispatch(getReportWithExcel({
      startDate: toStringByFormatting(new Date(form.dateRange[0])),
      endDate: toStringByFormatting(new Date(form.dateRange[1]))
    }))
  }

  const newReportList = () => {
    dispatch(getReportList({
      startDate: toStringByFormatting(new Date(form.dateRange[0])),
      endDate: toStringByFormatting(new Date(form.dateRange[1]))
    }))
  }

  const changeDirection = (key, value) => {
    const tempDirection = {}
    tempDirection[key] = value
    setSortDirection(tempDirection)
  }

  const filteredReportList = useMemo(() => {
    if (!reportList) return []
    const key = Object.keys(sortDirection)[0];
    if (!key) return reportList
    const value = sortDirection[key]
    let filteredUserList = [...reportList]; 
    filteredUserList.sort((aData, bData) => {
      let aInfo = aData[key];
      let bInfo = bData[key];
      if (typeof aInfo === 'boolean') aInfo = aInfo ? 'Y' : 'N';
      if (typeof bInfo === 'boolean') bInfo = bInfo ? 'Y' : 'N';
      let a = parseFloat(aInfo) || aInfo.toUpperCase();
      let b = parseFloat(bInfo) || bInfo.toUpperCase();
      if (value === 'ASC') {
        if (a < b) return 1;
        if (a > b) return -1;
        return 0;
      } else if (value === 'DESC') {
        if (a < b) return -1;
        if (a > b) return 1;
        return 0;
      }
      return 0;
    });
    return filteredUserList;
  }, [reportList, sortDirection]);

  useEffect(() => {
    dispatch(getReportList({
      startDate: toStringByFormatting(new Date(form.dateRange[0])),
      endDate: toStringByFormatting(new Date(form.dateRange[1]))
    }))
  }, [ dispatch, form.dateRange ])

  return (
    <>
      <TableContainer component={Paper}>
        <StyledSearch>
          <StyledSearchForm>
            <DateRangeForm
              dataKey={'dateRange'}
              displayName={''}
              form={form}
              handleChangeData={handleChangeData}
            />
          </StyledSearchForm>
          <StyledSearchBtn
            variant="contained"
            disableElevation
            disabled={!(form.dateRange[0] && form.dateRange[1])}
            onClick={newReportList}
          >
            조회
          </StyledSearchBtn>
        </StyledSearch>
        <Table sx={{ minWidth: 650 }} aria-label="simple table">
          <DynamicHead 
            list={headerList}
            sortDirection={sortDirection}
            changeDirection={changeDirection}
          />
          <TableBody>
            {
              filteredReportList.map(item => {
                return (
                  <TableItem item={item} key={item.reportId} />
                )
              })
            }
          </TableBody>
        </Table>
      </TableContainer>

      <ButtonGroup 
        direction="row"
        justifyContent="flex-end"
        spacing={1}
      >
        <ExcelButton variant="contained" disableElevation onClick={downloadExcels}>엑셀 다운로드</ExcelButton>
      </ButtonGroup>
    </>
  )
}

export default MonitoringTable